import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, KanbanSquare, Users, BarChart3, Megaphone,
    Mail, UserPlus, Send, BookOpen, Stethoscope, LogOut, Menu, X,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
    { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
    { to: "/admin/pipeline", label: "Pipeline", Icon: KanbanSquare },
    { to: "/admin/leads", label: "Leads", Icon: Users },
    { to: "/admin/analytics", label: "Analítica", Icon: BarChart3 },
    { to: "/admin/ads-setup", label: "Google Ads", Icon: Megaphone },
    { to: "/admin/emails", label: "Correos", Icon: Mail },
    { to: "/admin/subscribers", label: "Suscriptores", Icon: UserPlus },
    { to: "/admin/campaigns", label: "Campañas", Icon: Send },
    { to: "/admin/manual", label: "Manual", Icon: BookOpen },
    { to: "/admin/diagnostic", label: "Diagnóstico", Icon: Stethoscope },
];

export default function AdminLayout({ children, title, subtitle, testid, actions }) {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const user = (() => {
        try { return JSON.parse(localStorage.getItem("admin_user") || "{}"); }
        catch { return {}; }
    })();

    const logout = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex" data-testid={testid || "admin-layout"}>
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex w-60 bg-eclipse-primary text-white flex-col fixed left-0 top-0 bottom-0 z-30">
                <SidebarContent user={user} onLogout={logout} />
            </aside>

            {/* Mobile sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="left" className="w-64 p-0 bg-eclipse-primary text-white border-none">
                    <SidebarContent user={user} onLogout={logout} mobile onItemClick={() => setOpen(false)} />
                </SheetContent>
            </Sheet>

            {/* Main */}
            <div className="flex-1 lg:ml-60">
                <header className="bg-white border-b border-neutral-200 sticky top-0 z-20">
                    <div className="flex items-center justify-between h-16 px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                            <button onClick={() => setOpen(true)} className="lg:hidden p-2 -ml-2" data-testid="admin-mobile-menu">
                                <Menu className="w-5 h-5" />
                            </button>
                            <div className="flex flex-col">
                                <h1 className="font-display font-bold text-lg text-eclipse-primary leading-tight">{title}</h1>
                                {subtitle && <span className="text-xs text-eclipse-muted">{subtitle}</span>}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            {actions}
                            <span className="hidden sm:inline text-eclipse-muted">{user.nombre || user.email}</span>
                            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-neutral-300 hover:border-eclipse-cta hover:text-eclipse-cta text-eclipse-primary text-xs font-display font-bold uppercase tracking-wider">
                                <LogOut className="w-3.5 h-3.5" />
                                Salir
                            </button>
                        </div>
                    </div>
                </header>
                <main className="p-4 sm:p-6 lg:p-8 space-y-5">{children}</main>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Named exports usados por páginas (FASE 3 y 4)
// ---------------------------------------------------------------------------
export function KPICard({ label, value, icon: Icon, accent = "text-eclipse-primary", testid, sub }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-4 flex flex-col gap-1.5" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</span>
                {Icon && <Icon className={`w-4 h-4 ${accent}`} strokeWidth={2} />}
            </div>
            <div className={`text-2xl font-bold ${accent}`}>{value ?? "—"}</div>
            {sub && <div className="text-[11px] text-neutral-400">{sub}</div>}
        </div>
    );
}

export function StatusBadge({ label, color = "bg-neutral-100 text-neutral-600", bordered = true }) {
    return <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${color} ${bordered ? "border" : ""}`}>{label}</span>;
}

function SidebarContent({ user, onLogout, mobile = false, onItemClick }) {
    const location = useLocation();
    return (
        <>
            <div className="px-5 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <img src="/brand/eclipse_logo.png" alt="Eclipse Cubiertas Admin" className="h-12 w-auto object-contain" />
                </div>
                <p className="font-display font-medium text-[10px] tracking-[0.3em] text-eclipse-cta mt-2">CUBIERTAS · ADMIN</p>
            </div>
            <nav className="flex-1 overflow-y-auto py-4">
                {NAV.map(({ to, label, Icon, soon, end }) => {
                    const active = end ? location.pathname === to : location.pathname.startsWith(to);
                    return (
                        <Link
                            key={to}
                            to={to}
                            onClick={onItemClick}
                            data-testid={`sidebar-${to.replace("/admin/", "").replace("/admin", "dashboard")}`}
                            className={`flex items-center justify-between gap-3 px-5 py-2.5 text-sm font-display font-semibold transition-colors ${active ? "bg-eclipse-cta text-white" : "text-white/75 hover:text-white hover:bg-white/5"}`}
                        >
                            <span className="flex items-center gap-3">
                                <Icon className="w-4 h-4" strokeWidth={2} />
                                {label}
                            </span>
                            {soon && <span className="text-[9px] font-bold tracking-wider bg-white/15 px-1.5 py-0.5 rounded">PRONTO</span>}
                        </Link>
                    );
                })}
            </nav>
            <div className="px-5 py-4 border-t border-white/10 text-xs">
                <p className="text-white/60">Sesión</p>
                <p className="font-semibold truncate">{user.email}</p>
                <p className="text-eclipse-cta uppercase text-[10px] tracking-wider mt-1">{user.role}</p>
            </div>
        </>
    );
}
